<template>
  <div class="language-container">
    <div
      class="dropdown-trigger"
      :class="{
        active: isVisibleDropdown,
      }"
      @click="toggleDropdown"
    >
      <p>{{ selectedLanguageName }}</p>
      <component
        :is="isVisibleDropdown ? ChevronUpIcon : ChevronDownIcon"
        class="icon-chevron icon-medium icon-default icon-dropdown"
      />
    </div>
    <div v-if="isVisibleDropdown" v-click-outside="toggleDropdown" class="language-options">
      <LanguageList
        :languages="languageStore.getInterpretationLanguageOptions(chatType)"
        show-all-in-list
        selected-at-top
        is-using-language-detection
        @select="selectLanguage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ChevronDownIcon from '@/components/icons/IconChevronDown.vue';
import ChevronUpIcon from '@/components/icons/IconChevronUp.vue';
import LanguageList from '@/components/shared/base/LanguageList.vue';
import { getLanguageByCode } from '@/static/languages';
import { useLanguageStore } from '@/stores/language';
import { useToastStore } from '@/stores/toast';
import { Language, LanguageCode, Nullable, TypeCall } from '@/types/common';

const selectedLanguageCode = defineModel<Nullable<LanguageCode>>({
  required: true,
  default: null,
});

withDefaults(
  defineProps<{
    chatType: TypeCall;
  }>(),
  {
    chatType: 'GENERAL',
  }
);

const emit = defineEmits<{ select: [languageCode: LanguageCode] }>();

const { t } = useI18n();

const toastStore = useToastStore();
const languageStore = useLanguageStore();

const isVisibleDropdown = ref<boolean>(false);

const selectedLanguageName = computed<string>(() => {
  if (!selectedLanguageCode.value) {
    return t('choose_language');
  }

  const { name, code } = getLanguageByCode(selectedLanguageCode.value);
  const browserLanguageName = ` (${t(`label_language_${code}`)})`;

  return name + browserLanguageName || t('choose_language');
});

const selectLanguage = (lang: Language) => {
  try {
    selectedLanguageCode.value = lang.code;
    toggleDropdown();
    emit('select', lang.code);

    toastStore.info(lang.name, 'LANGUAGE');
  } catch {
    toastStore.error(t('toast_failed_to_change_language'), 'LANGUAGE');
  }
};

const toggleDropdown = () => {
  isVisibleDropdown.value = !isVisibleDropdown.value;
};
</script>

<style scoped lang="scss">
.dropdown-trigger {
  display: flex;
  width: 100%;
  gap: 0.5rem;
  justify-content: center;
  padding: 0.75rem;
  background-color: var(--surface-gray-dim);
  border-radius: 8px;
  cursor: pointer;
}

.dropdown-trigger:hover,
.dropdown-trigger:active,
.dropdown-trigger.active {
  background-color: var(--surface-gray-dim);
}

::v-deep(.dropdown-content) {
  width: 77dvw;
  max-width: 21rem;
}

.language-container {
  width: 100%;
  position: relative;
}

.language-options {
  position: absolute;
  width: 100%;
  z-index: 999;
  max-height: 300px;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: var(--surface-default);
  border-radius: 8px;
  overflow-y: auto;
}

.icon-chevron {
  position: absolute;
  right: 1rem;
}
</style>
